/**
 * Per-field AbortController + requestId registry.
 *
 * Plan Section "Race-condition cancellation":
 *   - Each fetch increments a UUID `requestId` and stores it on the field's state.
 *   - Aborts any prior `AbortController` for that field.
 *   - Resolves → checks `requestId` matches current. If mismatched, discarded silently.
 */
type Entry = { controller: AbortController; requestId: string };

let uuidCounter = 0;
function mintId(): string {
  uuidCounter += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${uuidCounter}`;
}

export class FetchRegistry {
  private map = new Map<string, Entry>();

  start(name: string): { signal: AbortSignal; requestId: string } {
    const prior = this.map.get(name);
    if (prior) {
      prior.controller.abort();
    }
    const controller = new AbortController();
    const requestId = mintId();
    this.map.set(name, { controller, requestId });
    return { signal: controller.signal, requestId };
  }

  isCurrent(name: string, requestId: string): boolean {
    return this.map.get(name)?.requestId === requestId;
  }

  abort(name: string): void {
    const entry = this.map.get(name);
    if (entry) {
      entry.controller.abort();
      this.map.delete(name);
    }
  }

  abortAll(): void {
    for (const [, entry] of this.map) {
      entry.controller.abort();
    }
    this.map.clear();
  }
}
